// Shared sheet shell for the per-kind attribute edit sheets (P1-18 PR B).
// Modeled after TagOverrideSheet.tsx — iOS modal, backdrop dismiss,
// scroll-safe form body, action rail along the bottom.
//
// Each per-kind sheet composes its own fields inside <AttrSheet> and
// supplies: title, saving flag, canSave flag, save fn, error, cancel fn.

import type { ReactNode } from "react";

export function AttrSheet({
  title,
  children,
  error,
  saving,
  canSave,
  onCancel,
  onSave,
}: {
  title: string;
  children: ReactNode;
  error: string | null;
  saving: boolean;
  canSave: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-end sm:place-items-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !saving) onCancel();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) onSave();
        }}
        className="w-full max-w-md max-h-[85vh] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="px-5 pt-5 pb-3 border-b border-slate-200/80 shrink-0">
          <h3 className="text-[17px] font-semibold text-slate-900">{title}</h3>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-4 flex-1">{children}</div>
        {error && (
          <p className="px-5 pb-2 text-[13px] text-[#ff3b30] shrink-0" role="alert">
            {error}
          </p>
        )}
        <div className="border-t border-slate-200/80 shrink-0">
          <button
            type="submit"
            disabled={!canSave}
            className="w-full h-[52px] text-[17px] font-semibold text-[#007aff] active:bg-slate-100 transition disabled:text-slate-400 disabled:active:bg-transparent"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="w-full h-[52px] text-[17px] text-slate-500 active:bg-slate-100 transition border-t border-slate-200/80 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// Shared plain-English error-message helper. Consumed by each per-kind
// sheet via a thin wrapper that maps the `field` name into a human label.
export function messageForEditError(
  err: { error: string; reason?: string; field?: string },
  humanFieldLabel: string,
): string {
  switch (err.error) {
    case "version_conflict":
      return "Someone else just edited this item. Close and reopen to see the latest.";
    case "validation_failed":
      switch (err.reason) {
        case "empty_after_trim":
          return `${humanFieldLabel} can't be empty.`;
        case "not_positive":
          return `${humanFieldLabel} must be a positive number.`;
        case "not_integer":
          return `${humanFieldLabel} must be a whole number.`;
        case "bad_enum":
          return `${humanFieldLabel} isn't one of the allowed values.`;
        case "not_boolean":
          return `${humanFieldLabel} must be on or off.`;
        default:
          return "That value isn't valid.";
      }
    case "not_found":
      return "This per-kind attribute block no longer exists for the item.";
    default:
      return "Couldn't save. Try again in a moment.";
  }
}
